import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Book, Loader, Mail } from 'lucide-react';
import { BookSampleProductionWrapper } from '../components/qr/BookSampleProductionWrapper';
import { getApiBase } from '../utils/api';

export default function BookSamplePage() {
  const { slug, accountSlug } = useParams<{ slug: string; accountSlug?: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [linkData, setLinkData] = useState<any>(null);
  const [showEmailGate, setShowEmailGate] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const API_BASE = getApiBase();
        const url = accountSlug 
          ? `${API_BASE}/api/public/link/${accountSlug}/${slug}`
          : `${API_BASE}/api/public/link/${slug}`;
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error('Link not found');
        }
        
        const data = await response.json();
        
        if (data.type !== 'sample') {
          throw new Error('Invalid link type');
        }
        
        // Check if expired
        if (data.metadata?.expiresAt && new Date(data.metadata.expiresAt) < new Date()) {
          throw new Error('Diese Leseprobe ist abgelaufen');
        }
        
        setLinkData(data);
        
        // Check if email gate is enabled
        if (data.metadata?.emailGateEnabled && !emailSubmitted) {
          // Check if user has already provided email (stored in localStorage)
          const storedEmail = localStorage.getItem(`qr_email_${data.id}`);
          if (storedEmail) {
            setCheckingAccess(true);
            try {
              const checkResponse = await fetch(
                `${API_BASE}/api/public/check-email/${data.id}/${encodeURIComponent(storedEmail)}`
              );
              
              if (checkResponse.ok) {
                const { hasAccess } = await checkResponse.json();
                if (hasAccess) {
                  setEmail(storedEmail);
                  setEmailSubmitted(true);
                  setShowEmailGate(false);
                } else {
                  // Email stored but not in database, show gate
                  setEmail(storedEmail);
                  setShowEmailGate(true);
                }
              } else {
                setShowEmailGate(true);
              }
            } catch (err) {
              console.error('Error checking email access:', err);
              setShowEmailGate(true);
            } finally {
              setCheckingAccess(false);
            }
          } else {
            setShowEmailGate(true);
          }
        }
        
        // Track page view
        await fetch(`${API_BASE}/api/public/track-analytics`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            qr_link_id: data.id,
            event_type: 'view',
            event_data: { page: 'book_sample' },
            visitor_id: localStorage.getItem('visitor_id') || `visitor_${Date.now()}`
          })
        });
      } catch (err) {
        console.error('Error:', err);
        setError(err instanceof Error ? err.message : 'Leseprobe nicht gefunden');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchData();
    }
  }, [slug, accountSlug, emailSubmitted]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkData || !email) return;

    try {
      const API_BASE = getApiBase();
      await fetch(`${API_BASE}/api/public/capture-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          qr_link_id: linkData.id,
          email,
          metadata: { source: 'book_sample_gate' }
        })
      });
      
      // Track email capture
      await fetch(`${API_BASE}/api/public/track-analytics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          qr_link_id: linkData.id,
          event_type: 'email_captured',
          event_data: { source: 'book_sample_gate' },
          visitor_id: localStorage.getItem('visitor_id') || `visitor_${Date.now()}`
        })
      });
      
      // Store email in localStorage for future visits
      localStorage.setItem(`qr_email_${linkData.id}`, email);
      
      setEmailSubmitted(true);
      setShowEmailGate(false);
    } catch (err) {
      console.error('Email capture error:', err);
    }
  };

  if (loading || checkingAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-blue-800">
            {checkingAccess ? 'Prüfe Zugriff...' : 'Lade Leseprobe...'}
          </p>
        </div>
      </div>
    );
  }

  if (error || !linkData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <Book className="w-20 h-20 text-blue-600/40 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Leseprobe nicht verfügbar</h1>
          <p className="text-gray-600 mb-6">{error || 'Diese Leseprobe existiert nicht.'}</p>
        </div>
      </div>
    );
  }

  // Email Gate
  if (showEmailGate && !emailSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <Book className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Leseprobe freischalten</h2>
            <p className="text-gray-600">
              Geben Sie Ihre E-Mail-Adresse ein, um die Leseprobe von "{linkData.metadata?.title}" zu lesen.
            </p>
          </div>
          
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Ihre E-Mail-Adresse"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
            <button
              type="submit"
              className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Leseprobe anzeigen
            </button>
            <p className="text-xs text-gray-500 text-center">
              Ihre E-Mail-Adresse wird nur für Updates zu diesem Buch verwendet.
            </p>
          </form>
        </div>
      </div>
    );
  }

  // Main content with BookSampleProductionWrapper
  return (
    <BookSampleProductionWrapper 
      linkId={slug}
      linkData={linkData}
      isPreview={false}
      accountSlug={accountSlug}
    />
  );
}
